import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BankData,
  ChargeDescriptionData,
  CountryData,
  CurrencyData,
  InvoiceData,
  InvoiceLineData,
  InvoiceTaxData,
  MessageData,
  OrderData,
  PartnerData,
  PartnerLocationData,
  ProductDescriptionData,
  TabData,
  UserRoleData,
} from '@compiere-ws/models/apiz-domains';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { InputInfo } from './fast-create-invoice-vendor-utils';

@Injectable()
export class FastCreateInvoiceVendorDataService {
  private readonly http = inject(HttpClient);

  private readonly baseUrl = `${environment.config.backend.ws.url}/specific/fast-create-invoice-vendor`;

  getPartnerData<T extends PartnerData[]>(vendorName: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/vendor/${vendorName}`);
  }

  getBankData<T extends BankData[]>({ IBAN, QRIBAN, bankAccountId }: InputInfo): Observable<T> {
    let params = new HttpParams();

    if (QRIBAN) {
      params = params.set('qrIban', QRIBAN);
    } else if (IBAN) {
      params = params.set('iban', IBAN);
    }

    if (bankAccountId) {
      params = params.set('bankAccountId', bankAccountId);
    }

    return this.http.get<T>(`${this.baseUrl}/banks`, { params });
  }

  getCurrencyData<T extends CurrencyData[]>(isoCode: string): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/currencies/${isoCode}`);
  }

  getCountryData<T extends CountryData[]>(countryCode: string, ADLanguage: string): Observable<T> {
    const params = new HttpParams().set('countryCode', countryCode).set('adLanguage', ADLanguage);
    return this.http.get<T>(`${this.baseUrl}/countries`, { params });
  }

  getInvoiceData<T extends InvoiceData[]>(invoiceId: number): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/invoices/${invoiceId}`);
  }

  getInvoiceLineData<T extends InvoiceLineData[]>(invoiceId: number): Observable<T> {
    const params = new HttpParams().set('invoiceId', invoiceId.toString());
    return this.http.get<T>(`${this.baseUrl}/invoice-lines`, { params });
  }

  getInvoiceTaxData<T extends InvoiceTaxData[]>(invoiceId: number): Observable<T> {
    const params = new HttpParams().set('invoiceId', invoiceId.toString());
    return this.http.get<T>(`${this.baseUrl}/invoice-taxes`, { params });
  }

  getOrderData<T extends OrderData[]>(
    partnerId: number,
    isReturnTrx: string,
    amountFrom: number,
    amountTo: number
  ): Observable<T> {
    const params = new HttpParams()
      .set('partnerId', partnerId.toString())
      .set('isReturnTrx', isReturnTrx)
      .set('amountFrom', amountFrom.toString())
      .set('amountTo', amountTo.toString());
    return this.http.get<T>(`${this.baseUrl}/orders`, { params });
  }

  getPartnerLocationData<T extends PartnerLocationData[]>({
    address1,
    postalCode,
    city,
    countryCode,
  }: InputInfo): Observable<T> {
    let params = new HttpParams();

    if (address1) {
      params = params.set('address1', address1);
    }
    if (postalCode) {
      params = params.set('postalCode', postalCode);
    }
    if (city) {
      params = params.set('city', city);
    }
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }

    return this.http.get<T>(`${this.baseUrl}/partner-locations`, { params });
  }

  getChargeDescription<T extends ChargeDescriptionData[]>(chargeId: number): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/charges/${chargeId}/description`);
  }

  getProductDescription<T extends ProductDescriptionData[]>(productId: number): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/products/${productId}/description`);
  }

  getUserRoleData<T extends UserRoleData[]>(userId: number, clientId: number): Observable<T> {
    const params = new HttpParams().set('userId', userId.toString()).set('clientId', clientId.toString());
    return this.http.get<T>(`${this.baseUrl}/user-roles`, { params });
  }

  getLogMessages<T extends MessageData[]>(ADLanguage: string): Observable<T> {
    const params = new HttpParams().set('adLanguage', ADLanguage);
    return this.http.get<T>(`${this.baseUrl}/messages`, { params });
  }

  getTabData<T extends TabData[]>(): Observable<T> {
    return this.http.get<T>(`${this.baseUrl}/tabs`);
  }

  getInvoiceZPayComData<T extends InvoiceData[]>(
    partnerId: number,
    paymentCommunication: string,
    excludeInvoiceId?: number
  ): Observable<T> {
    let params = new HttpParams()
      .set('partnerId', partnerId.toString())
      .set('paymentCommunication', paymentCommunication);

    if (excludeInvoiceId) {
      params = params.set('excludeInvoiceId', excludeInvoiceId.toString());
    }

    return this.http.get<T>(`${this.baseUrl}/invoices/by-payment-communication`, {
      params,
    });
  }
}
